import React, { useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { isNil, isUndefined } from 'lodash'

import { EndUser } from '@driftt/api-types'
import { Div } from '@driftt/tide-core'

import Card from 'components/Card'
import { SalesforceContext } from 'context/salesforceContext'
import GroupedAttributesCard from 'sections/CCRM/GroupedAttributesCard'
import { getEnabledIntegrations } from 'selectors'

import styles from './styles.module.css'

interface AttributesTabProps {
  user: EndUser
  salesforceData: any
  contactProperties: any
  isSalesforceLoading: boolean
}

const scrollToElement = (ref: React.MutableRefObject<HTMLDivElement | null>) => {
  if (ref?.current) {
    ref.current.scrollIntoView({ behavior: 'smooth' })
  }
}

const getUserFields = (
  properties: string[],
  definitions: Record<string, any>[],
  user: Record<string, any>
) => {
  return properties?.map((property: string) => {
    const definition = definitions?.find((definition: any) => definition.name === property)
    const value = !isNil(user[property]) ? user[property] : user.attributes[property]
    return { label: property, value, type: definition?.type }
  })
}

const getGeoIPFields = (properties: Record<string, any>[]) =>
  properties?.map((property: any) => ({
    label: property.displayName,
    value: property.value
  }))

const getDriftIntelFields = (data: Record<string, any[]>) =>
  data &&
  Object.entries(data)?.map(([key, value]: any) => ({
    label: key,
    value
  }))

const getSalesforceFields = (data: any, attributeDefs: any) =>
  attributeDefs?.map((attribute: any) => ({
    label: attribute.displayName,
    value: data?.cleanNameToValue[attribute.name]
  }))

const AttributesTab: React.FC<AttributesTabProps> = ({ user, salesforceData, contactProperties, isSalesforceLoading }) => {
  const [isDriftContactFieldsLoading, setIsDriftContactFieldsLoading] = useState(true)
  const attributeDefs = useContext(SalesforceContext)
  const enabledIntegrations = useSelector(getEnabledIntegrations)

  const sfdcContactDataCard = useRef<HTMLDivElement>(null)
  const driftAttributesCard = useRef<HTMLDivElement>(null)
  const customAttributesCard = useRef<HTMLDivElement>(null)
  const geoIpAttributesCard = useRef<HTMLDivElement>(null)
  const driftIntelPersonalCard = useRef<HTMLDivElement>(null)
  const driftIntelCompanyCard = useRef<HTMLDivElement>(null)

  const {
    propertyDefinitions,
    driftProperties,
    customProperties,
    geoIPProperties,
    driftIntel
  } = contactProperties

  const hasSalesforceConnected = enabledIntegrations['salesforce']
  const hasDerivedObjects = !!driftIntel?.derivedObjects?.[1]
  const personalDataPresent =
    hasDerivedObjects && driftIntel?.derivedObjects?.[1]?.value?.personalDataPresent
  const companyDataPresent =
    hasDerivedObjects && driftIntel?.derivedObjects[1]?.value?.companyDataPresent

  const derivedIntel = driftIntel?.derivedObjects?.[1]?.value

  const salesforceFields = getSalesforceFields(salesforceData, attributeDefs)

  const driftUserFields = getUserFields(driftProperties, propertyDefinitions, user)
  const customUserFields = getUserFields(customProperties, propertyDefinitions, user)

  const geoIPFields = getGeoIPFields(geoIPProperties)
  const personalIntelFields = getDriftIntelFields(derivedIntel?.personalData)
  const companyIntelFields = getDriftIntelFields(derivedIntel?.companyData)

  useEffect(() => {
    setIsDriftContactFieldsLoading(isUndefined(driftProperties))
  }, [driftProperties])
  return (
    <Div className={styles.container}>
      <Card className={styles.quickLinks}>
        <p>JUMP TO</p>
        {hasSalesforceConnected && (
          <button onClick={() => scrollToElement(sfdcContactDataCard)}>Salesforce</button>
        )}
        <button onClick={() => scrollToElement(driftAttributesCard)}>Drift Attributes</button>
        <button onClick={() => scrollToElement(customAttributesCard)}>Custom Attributes</button>
        <button onClick={() => scrollToElement(geoIpAttributesCard)}>Geo IP</button>
        {personalDataPresent && (
          <button onClick={() => scrollToElement(driftIntelPersonalCard)}>
            Drift Intel Personal
          </button>
        )}
        {companyDataPresent && (
          <button onClick={() => scrollToElement(driftIntelCompanyCard)}>
            Drift Intel Company
          </button>
        )}
      </Card>
      <Div className={styles.mainContent}>
        {hasSalesforceConnected && (
          <GroupedAttributesCard
            headerText="Salesforce"
            attributes={salesforceData && salesforceFields}
            emptyStateText="No matching contact in Salesforce"
            forwardRef={sfdcContactDataCard}
            isLoading={isSalesforceLoading}
          />
        )}
        <GroupedAttributesCard
          headerText="Drift attributes"
          attributes={driftUserFields}
          emptyStateText="No Drift attributes for this contact"
          forwardRef={driftAttributesCard}
          isLoading={isDriftContactFieldsLoading}
        />
        <GroupedAttributesCard
          headerText="Custom attributes"
          attributes={customUserFields}
          emptyStateText="No custom attributes for this contact"
          forwardRef={customAttributesCard}
          isLoading={isDriftContactFieldsLoading}
        />
        <GroupedAttributesCard
          headerText="Geo IP"
          attributes={geoIPFields}
          emptyStateText="No Geo IP information for this contact"
          forwardRef={geoIpAttributesCard}
          isLoading={isDriftContactFieldsLoading}
        />
        {personalDataPresent && (
          <GroupedAttributesCard
            headerText="Drift Intel Personal"
            attributes={personalIntelFields}
            emptyStateText="No Drift Intel for this contact"
            forwardRef={driftIntelPersonalCard}
            isLoading={isDriftContactFieldsLoading}
          />
        )}
        {companyDataPresent && (
          <GroupedAttributesCard
            headerText="Drift Intel Company"
            attributes={companyIntelFields}
            emptyStateText="No Drift Intel for this company"
            forwardRef={driftIntelCompanyCard}
            isLoading={isDriftContactFieldsLoading}
          />
        )}
      </Div>
    </Div>
  )
}

export default AttributesTab
